import { useLingui } from '@lingui/react/macro';
import { Dictionary, identity, keyBy, memoize } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DictValue, Dicts, configService } from 'services';

function formatOptions<V = string>(
  dictionary: Dictionary<DictValue>,
  {
    nameFormat = identity,
    valueFormat = identity as (v: string) => V
  }: { nameFormat?: typeof identity; valueFormat?: (v: string) => V } = {}
) {
  return Object.keys(dictionary).map((Code) => {
    const { Name } = dictionary[Code];
    return {
      value: valueFormat(Code),
      label: nameFormat(Name)
    };
  });
}

const prepareDictionaries = memoize((data: Dicts | undefined) => ({
  currencies: keyBy(data?.currency, (record) => record.Code),
  operators: keyBy(data?.mobileoperators, (record) => record.Code),
  mainboards: keyBy(data?.mainboards, (record) => record.Value),
  devicetype: keyBy(data?.devicetype, (record) => record.Code),
  timezone: keyBy(
    data?.timezones.map(({ utc_offset, timezone, ...record }) => ({
      Code: timezone,
      Name: `${timezone} ${utc_offset}`,
      utc_offset,
      ...record
    })),
    (record) => record.Code
  ),
  logfilters: keyBy(data?.logfilters, (record) => record.Code),
  countrycodes: keyBy(
    data?.countrycodes.map(({ Name, Value }) => ({ Name, Code: Value })),
    (record) => record.Code
  ),
  roles: keyBy(
    data?.roles?.map(({ GroupName }) => ({
      Code: GroupName,
      Name: GroupName
    })),
    (record) => record.Code
  ),
  cartridges: data?.cartridges ?? {},
  fiscal: keyBy(
    data?.fiscal?.map((item, index) => ({ ...item, Code: String(index) })) ??
      [],
    (record) => record.Code
  )
}));

const prepareOptions = memoize(
  (dictionaries: ReturnType<typeof prepareDictionaries>) => ({
    currencies: formatOptions(dictionaries.currencies),
    operators: formatOptions(dictionaries.operators),
    mainboards: Object.entries(dictionaries.mainboards).map(
      ([value, mainboard]) => ({
        value: +value,
        label: mainboard.Name
      })
    ),
    devicetype: formatOptions(dictionaries.devicetype),
    timezone: formatOptions(dictionaries.timezone),
    countrycodes: formatOptions(dictionaries.countrycodes),
    roles: formatOptions(dictionaries.roles),
    fiscal: formatOptions(dictionaries.fiscal, {
      valueFormat: (v) => Number(v)
    })
  })
);

export function useLoadConfig() {
  const { i18n } = useLingui();
  const { isLoading } = configService.useAllQuery(i18n.locale);
  return isLoading;
}

export function useConfig() {
  const { i18n } = useLingui();
  const dataSelector = useMemo(
    () => configService.endpoints.all.select(i18n.locale),
    [i18n.locale]
  );
  const { data } = useSelector(dataSelector);

  const dictionaries = prepareDictionaries(data);
  const options = prepareOptions(dictionaries);

  return { dictionaries, options };
}
